/**
 * 크롤링 작업 관리를 위한 커스텀 훅
 * 
 * 이 훅은 크롤링 작업 목록을 가져오고, 새 작업 추가, 삭제, 상태 갱신 등
 * 크롤링 작업 관련 API와의 통신을 처리합니다.
 * 
 * 제공하는 기능:
 * - 크롤링 작업 목록 조회 및 상태 관리
 * - 새 크롤링 작업 추가
 * - 작업 삭제
 * - 작업 상태 갱신
 * - 작업 재시작
 * - 작업 완료 처리
 * 
 * @returns {Object} 크롤링 작업 관련 상태와 함수를 제공하는 객체
 */

import { useState, useEffect, useCallback } from 'react';
import crawlApi from '../api/crawlApi';

export default function useCrawlJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * 크롤링 작업 목록을 서버에서 가져옵니다.
   * 
   * @async
   * @returns {Promise<void>}
   */
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await crawlApi.listCrawls();
      setJobs(data.crawls || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 새 크롤링 작업을 추가합니다.
   * 
   * @async
   * @param {Object} jobRequest - 크롤링 작업 요청 정보
   * @param {string} jobRequest.url - 크롤링할 URL
   * @param {number} [jobRequest.depth=2] - 크롤링 깊이
   * @param {boolean} [jobRequest.follow_links=true] - 링크 따라가기 여부
   * @returns {Promise<Object>} 생성된 작업 정보
   * @throws {Error} API 요청 실패 시 발생
   */
  const addJob = useCallback(async (jobRequest) => {
    setLoading(true);
    setError(null);
    try {
      const newJob = await crawlApi.startCrawl(jobRequest);
      setJobs((prev) => [newJob, ...prev]);
      return newJob;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 크롤링 작업을 삭제합니다.
   * 
   * @async
   * @param {string} jobId - 삭제할 작업 ID
   * @returns {Promise<void>}
   * @throws {Error} API 요청 실패 시 발생
   */
  const deleteJob = useCallback(async (jobId) => {
    setLoading(true);
    setError(null);
    try {
      await crawlApi.deleteCrawl(jobId);
      setJobs((prev) => prev.filter((job) => job.crawl_id !== jobId));
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 특정 크롤링 작업의 상태를 갱신합니다.
   * 
   * @async
   * @param {string} jobId - 상태를 갱신할 작업 ID
   * @returns {Promise<void>}
   */
  const refreshJob = useCallback(async (jobId) => {
    try {
      const updated = await crawlApi.getCrawlStatus(jobId);
      setJobs((prev) => prev.map((job) => job.crawl_id === jobId ? updated : job));
    } catch (err) {
      setError(err);
    }
  }, []);

  /**
   * 실패하거나 중단된 크롤링 작업을 재시작합니다.
   * 
   * @async
   * @param {string} jobId - 재시작할 작업 ID
   * @returns {Promise<Object>} 업데이트된 작업 정보
   * @throws {Error} API 요청 실패 시 발생
   */
  const restartJob = useCallback(async (jobId) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await crawlApi.restartCrawl(jobId);
      setJobs((prev) => prev.map((job) => job.crawl_id === jobId ? updated : job));
      return updated;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 크롤링 작업을 완료 상태로 표시합니다.
   * 
   * @async
   * @param {string} jobId - 완료로 표시할 작업 ID
   * @returns {Promise<Object>} 업데이트된 작업 정보
   * @throws {Error} API 요청 실패 시 발생
   */
  const markAsCompleted = useCallback(async (jobId) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await crawlApi.markAsCompleted(jobId);
      setJobs((prev) => prev.map((job) => job.crawl_id === jobId ? updated : job));
      return updated;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // 컴포넌트 마운트 시 작업 목록 로드
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  return {
    jobs,
    loading,
    error,
    fetchJobs,
    addJob,
    deleteJob,
    refreshJob,
    restartJob,
    markAsCompleted,
    setJobs, // 필요시 외부에서 직접 상태 조작
  };
} 